import React, { useCallback, useId, useRef } from "react";

/**
 * Accessible segmented control — use for 2–4 mutually exclusive options.
 * Keyboard: Tab to focus, ←/→ to switch, Home/End for first/last.
 */
const SegmentToggle = ({
  label,
  options,
  value,
  onChange,
  className = "",
  fullWidth = false,
}) => {
  const groupId = useId();
  const buttonRefs = useRef([]);

  const focusOption = useCallback((index) => {
    const btn = buttonRefs.current[index];
    btn?.focus({ preventScroll: true });
  }, []);

  const selectByIndex = useCallback(
    (index) => {
      const opt = options[index];
      if (!opt || opt.value === value) return;
      onChange(opt.value);
      focusOption(index);
    },
    [options, value, onChange, focusOption]
  );

  const handleKeyDown = (e, index) => {
    const last = options.length - 1;
    if (last < 0) return;

    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = index >= last ? 0 : index + 1;
      selectByIndex(next);
      return;
    }
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = index <= 0 ? last : index - 1;
      selectByIndex(prev);
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      selectByIndex(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      selectByIndex(last);
    }
  };

  const activeIndex = Math.max(
    0,
    options.findIndex((opt) => opt.value === value)
  );

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <span
          id={`${groupId}-label`}
          className="text-xs font-semibold uppercase tracking-wide text-muted"
        >
          {label}
        </span>
      )}
      <div
        className={`inline-flex p-1 bg-subtle rounded-lg border border-line ${
          fullWidth ? "w-full" : ""
        }`}
        role="radiogroup"
        aria-labelledby={label ? `${groupId}-label` : undefined}
        aria-label={label || undefined}
      >
        {options.map((opt, index) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              ref={(el) => {
                buttonRefs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => onChange(opt.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={`flex-1 px-4 py-2 text-sm font-semibold rounded-md transition-all duration-200 whitespace-nowrap focus-visible:ring-2 focus-visible:ring-main/30 focus-visible:outline-none ${
                active
                  ? opt.activeClass ||
                    "bg-main text-white shadow-soft ring-1 ring-main/20"
                  : "text-inkSecondary hover:text-ink hover:bg-card/80"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentToggle;
